import Image from '@tiptap/extension-image';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { InlineImageView } from './InlineImageView';

/**
 * Inline image node for pictures embedded mid-paragraph/mid-cell (markdown
 * `![alt](url)`, or raw `<img src>`) — e.g. an equation exported as an
 * image — as opposed to a standalone `figure` block. A distinct node name
 * from `FigureImage`'s `image` (ProseMirror requires unique node names),
 * kept inline so surrounding text still flows around it.
 *
 * Same `data-*`-not-`src` convention as `FigureImage`: the href travels as
 * `data-inline-href`, rewritten from a plain `src` by `sanitizeHtml` (see
 * markdown.ts) so an `s3://` href never reaches a real `src` attribute.
 */
export const InlineImage = Image.extend({
  name: 'inlineImage',
  inline: true,
  group: 'inline',

  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        parseHTML: () => null,
        renderHTML: () => ({}),
      },
      'data-inline-href': {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-inline-href'),
        renderHTML: (attributes: Record<string, unknown>) => {
          const value = attributes['data-inline-href'];

          return value ? { 'data-inline-href': value } : {};
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[data-inline-href]' }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(InlineImageView);
  },
});
